const destinations = [
  {
    name: "France & French Caribbean",
    context: "Cajun heritage tours, Festival International connections, ULL exchange programs in Paris and Quebec",
    urgency: "Heritage trips often planned 4–8 weeks out; festival travel compressed further",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "West Africa (Angola, Nigeria)",
    context: "Oilfield rotation assignments for Lafayette-based energy service company engineers and contractors",
    urgency: "Rotation orders often arrive with 10–14 days to report",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Bahamas & Caribbean",
    context: "New Orleans and Galveston cruise sailings, spring break trips, family vacation bookings from Acadiana",
    urgency: "Last-minute cruise deals come together in 2–4 weeks",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "North Sea & Norway",
    context: "Offshore platform assignments, energy sector technology transfer, subsea engineering projects",
    urgency: "Offshore rotation timelines are non-negotiable — vessels depart on schedule",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Canada (Quebec)",
    context: "Cajun-Acadian cultural exchange, Festival connections, Lafayette family heritage ties to Nova Scotia",
    urgency: "Summer festival season drives June–August passport renewals",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Middle East (Qatar, UAE)",
    context: "LNG project assignments, energy conference travel, petrochemical technology licensing negotiations",
    urgency: "Project assignment confirmations often arrive with less than 3 weeks notice",
    icon: "ri-map-pin-2-line",
  },
];

const commonSituations = [
  {
    stat: "72%",
    label: "of Lafayette expedited requests are energy industry or academic and discovered within 3 weeks of travel",
  },
  {
    stat: "10–14 Days",
    label: "typical notice Lafayette oilfield engineers receive before an international rotation assignment",
  },
  {
    stat: "Oct–Feb",
    label: "peak study-abroad passport season when ULL students rush to process before spring semester departures",
  },
  {
    stat: "130 Miles",
    label: "to the Port of New Orleans — where many Acadiana families board Caribbean and Bahamas cruises",
  },
];

export default function LafayetteDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Lafayette Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from Acadiana clients. Understanding local travel behavior is part of why our document review catches issues before they derail your rotation, cruise, or heritage trip.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Louisiana Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across Louisiana with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/new-orleans" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                New Orleans <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/baton-rouge" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Baton Rouge <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/shreveport" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Shreveport <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}